import React, { useState } from "react";
import {
  Box,
  Button,
  Paper,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

import { PlannerLayout } from "../../layouts";

import {
  EventData,
  EventSummary,
  EventVenueDetails,
} from "./views/createEvent";
import { EventData as EventDataInterface } from "../../interfaces/planner.interface";

import { createNewEvent } from "../../api/providers/plannerEventsProviders";

const steps = ["Datos Generales", "Ubicacion en el mapa", "Resumen"];
export const CreateEventPage = () => {
  const [activeStep, setActiveStep] = React.useState(0);
  const [skipped, setSkipped] = React.useState(new Set<number>());
  const [eventData, setEventData] = useState<EventDataInterface>({
    name: "",
    bannerAlt: { file: null, preview: null },
    banner: { file: null, preview: null },
    description: "",
    categoryId: 0,
    address: "",
    id: 0,
    startDate: "",
    endDate: "",
    latitude: 9.953158,
    longitude: -84.086716,
    venueDetails: [],
  });
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const createEvent = async () => {
    try {
      setLoading(true);

      const formattedEventData = {
        ...eventData,
        startDate: new Date(eventData.startDate).toISOString().split("T")[0],
        endDate: new Date(eventData.endDate).toISOString().split("T")[0],
      };

      const result = await createNewEvent(formattedEventData);
      if (result.ok) {
        navigate("/planner/events");
      }
    } catch (error) {
      console.error("Error creating the event:", error);
    } finally {
      setLoading(false);
    }
  };

  const isStepOptional = (step: number) => {
    return step === 1 || step === 2 || step === 3 || step === 4;
  };

  const isStepSkipped = (step: number) => {
    return skipped.has(step);
  };

  const handleNext = () => {
    let newSkipped = skipped;
    if (isStepSkipped(activeStep)) {
      newSkipped = new Set(newSkipped.values());
      newSkipped.delete(activeStep);
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setSkipped(newSkipped);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSkip = () => {
    if (!isStepOptional(activeStep)) {
      throw new Error("You can't skip a step that isn't optional.");
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setSkipped((prevSkipped) => {
      const newSkipped = new Set(prevSkipped.values());
      newSkipped.add(activeStep);
      return newSkipped;
    });
  };

  const handleReset = () => {
    setActiveStep(0);
  };

  const handleFinish = () => {
    createEvent();
  };

  return (
    <PlannerLayout title="eventos">
      <Box sx={{ mb: 2 }}>
        <Typography fontSize={40} color={"white"}>
          Crear Evento
        </Typography>
      </Box>

      <Paper
        sx={{ overflow: "hidden", padding: 5, borderRadius: 2, mt: 3 }}
        elevation={6}
      >
        <Box sx={{ width: "100%" }}>
          <Stepper activeStep={activeStep}>
            {steps.map((label, index) => {
              const stepProps: { completed?: boolean } = {};
              const labelProps: {
                optional?: React.ReactNode;
              } = {};
              if (isStepOptional(index)) {
                labelProps.optional = (
                  <Typography variant="caption">Opcional</Typography>
                );
              }
              if (isStepSkipped(index)) {
                stepProps.completed = false;
              }
              return (
                <Step key={label} {...stepProps}>
                  <StepLabel
                    {...labelProps}
                    StepIconProps={{
                      sx: {
                        color: activeStep === index ? "secondary" : "grey.400",
                      },
                    }}
                  >
                    {label}
                  </StepLabel>
                </Step>
              );
            })}
          </Stepper>

          {activeStep === steps.length ? (
            <>
              <Typography sx={{ mt: 2, mb: 1 }}>
                All steps completed - you&apos;re finished
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
                <Box sx={{ flex: "1 1 auto" }} />
                <Button onClick={handleReset}>Reset</Button>
              </Box>
            </>
          ) : (
            <>
              {activeStep === 0 && (
                <EventData
                  eventData={eventData}
                  setEventData={setEventData}
                  setLoading={setLoading}
                />
              )}

              {activeStep === 1 && (
                <EventVenueDetails
                  eventData={eventData}
                  setEventData={setEventData}
                />
              )}

              {activeStep === 2 && (
                <EventSummary eventData={eventData} loading={loading} />
              )}

              <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
                <Button
                  color="inherit"
                  disabled={activeStep === 0}
                  onClick={handleBack}
                  sx={{ mr: 1 }}
                >
                  Back
                </Button>

                <Box sx={{ flex: "1 1 auto" }} />
                {isStepOptional(activeStep) && (
                  <Button color="inherit" onClick={handleSkip} sx={{ mr: 1 }}>
                    Skip
                  </Button>
                )}

                <Button
                  onClick={
                    activeStep === steps.length - 1 ? handleFinish : handleNext
                  }
                >
                  {activeStep === steps.length - 1 ? "Finish" : "Next"}
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Paper>
    </PlannerLayout>
  );
};
